import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { EmbeddedMessage } from 'constants/messageMediums';
import { TemplateSource, TemplateType } from 'constants/templateTypes';
import {
  selectIsEmbeddedMessagingJourneysFeatureFlagOn,
  selectIsSupportChatTestFeatureFlagOn,
} from 'core/userContext';
import { AllMessageMedium, CampaignType } from 'modules/domains/campaign';
import { MessageMedium } from 'modules/graphql/types';
import { TemplateMetadata } from 'modules/templateEditor/common/types';
import { useTemplateQueryContext } from 'modules/templateEditor/context/TemplateQueryContext';
import { addNegativeToast } from 'modules/toastContainer';
import { ChatButton } from 'ui/button';
import { Takeover } from 'ui/workflows/takeover';
import { useDebouncedState } from 'utils/hooks/useDebouncedState';
import noop from 'utils/noop';

import { ConsumingApp } from '../InStudioTemplateEditing.types';
import { useInStudioTakeoverContext } from '../context';
import { displayContent } from './AttachTemplateTakeover.constant';
import { AttachTemplateTakeoverBody } from './AttachTemplateTakeoverBody';
import { AttachTemplateTakeoverFooter } from './AttachTemplateTakeoverFooter';

type AttachTemplateTakeoverProps = {
  /** string representation of which type of templates to display */
  messageMedium: AllMessageMedium;
  /** The campaign's campaignType definition */
  campaignType: CampaignType;
  /** Callback when the takeover is closed */
  onClose?: () => void;
  /** Selected template id */
  attachedTemplateId?: number;
  /** Callback when Attach Template is clicked */
  onAttachTemplate: (template: { id: number; locale: string }, dataFeedId: number | null) => void;
  /** Which app is using this takeover  */
  consumingApp?: ConsumingApp;
  /** campaign id */
  campaignId?: number;
  /** The placement id -- currently only used in embedded messaging */
  placementId?: number;
};

export const AttachTemplateTakeover = ({
  messageMedium = MessageMedium.Email,
  campaignType,
  onClose = noop,
  consumingApp,
  attachedTemplateId,
  onAttachTemplate,
  campaignId,
  placementId,
}: AttachTemplateTakeoverProps) => {
  const dispatch = useDispatch();

  const isEmbeddedJourneysOn = useSelector(selectIsEmbeddedMessagingJourneysFeatureFlagOn);
  const [showLoading, setShowLoading] = useState(false);

  const [selectedTab, setSelectedTab] = useState<TemplateSource>(TemplateSource.Base);
  const [templateType, setTemplateType] = useState<TemplateType[]>([]);
  const [debouncedSearch, search, setSearch] = useDebouncedState('', 500);

  // this value is null when re-loading the takeover after attaching
  // a template; it is only updated when a new selection is made
  // in the current instantiation of the template picker
  // (i.e. *changing* your template selection)
  const [selectedTemplate, setSelectedTemplate] = useState<TemplateMetadata | null>(null);
  const [selectedDataFeed, setSelectedDataFeed] = useState<number | null>(null);

  const { templateId } = useInStudioTakeoverContext();
  const { hasError, medium } = useTemplateQueryContext();

  useEffect(() => {
    if (hasError && templateId) setShowLoading(true);
  }, [hasError, templateId]);

  const isCampaignEditorConsumingApp = consumingApp === ConsumingApp.CAMPAIGN_EDITOR;
  // code for ada/ zendesk support chat
  const isSupportChatTestEnabled = useSelector(selectIsSupportChatTestFeatureFlagOn);
  const shouldDisplayImportHTMLButton =
    !!campaignId &&
    isCampaignEditorConsumingApp &&
    (messageMedium === MessageMedium.Email || messageMedium === MessageMedium.InApp);

  const showWorkflow = medium === EmbeddedMessage ? isEmbeddedJourneysOn : true;

  return (
    <Takeover
      onClose={onClose}
      title={displayContent.takeoverTitle}
      isMainApp={isCampaignEditorConsumingApp}
      isOverlay={isCampaignEditorConsumingApp}
      HeaderBarContent={isSupportChatTestEnabled && <ChatButton />}
      Body={
        <AttachTemplateTakeoverBody
          showLoading={showLoading}
          selectedTab={selectedTab}
          setSelectedTab={setSelectedTab}
          showWorkflow={showWorkflow}
          search={search}
          setSearch={setSearch}
          filter={debouncedSearch}
          setSelectedDataFeed={setSelectedDataFeed}
          selectedTemplate={selectedTemplate}
          setSelectedTemplate={setSelectedTemplate}
          messageMedium={messageMedium}
          campaignType={campaignType}
          templateId={attachedTemplateId}
          placementId={placementId}
          useDataFeeds={isCampaignEditorConsumingApp}
          templateType={templateType}
          setTemplateType={setTemplateType}
        />
      }
      Footer={
        <AttachTemplateTakeoverFooter
          campaignId={campaignId}
          showLoading={showLoading}
          selectedDataFeed={selectedDataFeed}
          shouldDisplayImportHTMLButton={shouldDisplayImportHTMLButton}
          onSelectTemplate={() => {
            if (selectedTemplate) {
              setShowLoading(true);
              onAttachTemplate(
                { id: selectedTemplate.id, locale: selectedTemplate.locale },
                selectedDataFeed,
              );
            } else {
              dispatch(addNegativeToast('Please select a template to attach to this campaign.'));
            }
          }}
        />
      }
    />
  );
};
